import { __UNSAFE_getMutableStreamingState } from './ui-streaming-state.ts';

export const ROW_RENDER_DELAY_MS = 700;

export type StreamingStateContext =
  | { kind: 'aifc' }
  | { kind: 'ai-field'; fieldId: string };

export class StreamingStateManager {
  private fieldsSeen = new Set<string>();
  private state = __UNSAFE_getMutableStreamingState();

  constructor(
    private readonly context: StreamingStateContext = {
      kind: 'aifc'
    }
  ) {
    this.state.processedValues.clear();
  }

  setEntryIdForProcessing(entryId: string): void {
    this.state.entryIdSelectedForProcessing = entryId;
    if (this.context.kind === 'ai-field') {
      this.state.activeAiFieldCell = {
        fieldId: this.context.fieldId,
        entryId
      };
    }
  }

  clearEntryIdForProcessing(): void {
    this.state.entryIdSelectedForProcessing = undefined;
    this.state.activeAiFieldCell = null;
  }

  setFieldIdForProcessing(fieldId: string): void {
    this.state.fieldIdSelectedForProcessing = fieldId;
  }

  clearFieldIdForProcessing(): void {
    setTimeout(() => {
      this.state.fieldIdSelectedForProcessing = undefined;
    }, ROW_RENDER_DELAY_MS);
  }

  markValueProcessed(fieldId: string, entryId: string): void {
    this.state.addProcessedValue(fieldId, entryId);
    if (
      this.state.activeAiFieldCell?.fieldId === fieldId &&
      this.state.activeAiFieldCell.entryId === entryId
    ) {
      this.state.activeAiFieldCell = null;
    }
  }

  markFieldSeen(fieldId: string): void {
    this.fieldsSeen.add(fieldId);
    this.state.isSingleFieldStreaming = this.fieldsSeen.size === 1;
  }

  /**
   * Sets isSingleFieldStreaming to false, dismissing the column gradient so
   * that per-row / per-cell highlights can activate without conflict.
   *
   * Called when genuine entry processing begins (ADD_ENTRY / EDIT_ENTRY tokens),
   * which is the only correct phase-transition point between field-level and
   * entry-level highlighting.
   */
  resetSingleFieldStreaming(): void {
    this.state.isSingleFieldStreaming = false;
  }

  markFieldsComplete(): void {
    this.state.fieldsAreFinishedStreaming = true;
  }

  markFirstLayoutProcessed(): void {
    if (!this.state.firstLayoutProcessed) {
      this.state.firstLayoutProcessed = true;
    }
  }

  setEntryOperation(operation: 'edit' | 'delete' | undefined): void {
    this.state.currentEntryOperation = operation;
  }

  setFieldOperation(operation: 'delete' | undefined): void {
    this.state.currentFieldOperation = operation;
  }

  scheduleFieldDeletion(fieldId: string, deleteCallback: () => void): void {
    this.state.fieldIdSelectedForProcessing = fieldId;
    this.state.currentFieldOperation = 'delete';

    setTimeout(() => {
      deleteCallback();
      this.state.fieldIdSelectedForProcessing = undefined;
    }, ROW_RENDER_DELAY_MS);
  }

  scheduleEntryDeletion(entryId: string, deleteCallback: () => void): void {
    this.state.currentEntryOperation = 'delete';
    this.setEntryIdForProcessing(entryId);

    setTimeout(() => {
      deleteCallback();
      this.clearEntryIdForProcessing();
    }, ROW_RENDER_DELAY_MS);
  }

  resetOnStreamingEnd(): void {
    this.clearEntryIdForProcessing();
    this.clearFieldIdForProcessing();
    this.fieldsSeen.clear();
    setTimeout(() => {
      this.state.isSingleFieldStreaming = undefined;
    }, ROW_RENDER_DELAY_MS);
    this.state.currentFieldOperation = undefined;
    this.state.currentEntryOperation = undefined;
  }
}
